/**
 * Service Map Renderer
 * Renders service map visualizations to the UI
 *
 * @module ServiceMapRenderer
 * Sprint 32: OOA/OOD Refactoring
 */

import {
    ServiceMap,
    ServiceMapTier,
    ColorScheme,
    TierColors
} from '../types/ii-agent-types';
import { safeFeatherReplace, sleep } from '../utils/html-utils';

/**
 * Interface for service map renderer
 */
export interface IServiceMapRenderer {
    displayServiceMap(serviceMap: ServiceMap, container: HTMLElement): Promise<void>;
    showLog(): void;
    closeLog(): void;
    copyLogToClipboard(): Promise<void>;
}

/**
 * Renderer for service map visualizations
 */
export class ServiceMapRenderer implements IServiceMapRenderer {
    private currentServiceMap: ServiceMap | null = null;

    /**
     * Display service map visualization with progressive rendering
     */
    async displayServiceMap(serviceMap: ServiceMap, container: HTMLElement): Promise<void> {
        console.log('Displaying service map:', serviceMap);
        this.currentServiceMap = serviceMap;

        // Clear existing map first
        container.innerHTML = '';
        container.classList.remove('hidden');

        if (!serviceMap || !serviceMap.tiers) {
            container.innerHTML = `
                <div class="alert alert-warning">
                    <i data-feather="alert-triangle"></i>
                    <span>No service map data available</span>
                </div>
            `;
            safeFeatherReplace();
            return;
        }

        // Create container structure
        const mapWrapper = document.createElement('div');
        mapWrapper.className = 'max-w-4xl mx-auto';

        const headerDiv = this.createHeader(serviceMap);
        const statusDiv = this.createStatusIndicator();
        const tiersContainer = document.createElement('div');
        tiersContainer.id = 'service-map-tiers-container';
        tiersContainer.className = 'flex flex-col items-center space-y-0';

        mapWrapper.appendChild(headerDiv);
        mapWrapper.appendChild(statusDiv);
        mapWrapper.appendChild(tiersContainer);
        container.appendChild(mapWrapper);

        // Render tiers progressively with delays
        if (serviceMap.tiers && serviceMap.tiers.length > 0) {
            for (let index = 0; index < serviceMap.tiers.length; index++) {
                const tier = serviceMap.tiers[index];
                const tierNames: Record<number, string> = {
                    1: 'Load Balancer',
                    2: 'Application',
                    3: 'Database',
                    4: 'Storage'
                };
                const tierName = tierNames[tier.tier_level] || tier.tier_name;

                // Update status before delay
                this.updateStatus(statusDiv, `Preparing ${tierName} tier...`, 'blue', index + 1, serviceMap.tiers.length);

                // 10 second delay before rendering
                console.log(`Waiting 10 seconds before rendering ${tierName} tier...`);
                await sleep(10000);

                // Update status during rendering
                this.updateStatus(statusDiv, `Rendering ${tierName} tier with ${tier.devices?.length || 0} device(s)...`, 'green', index + 1, serviceMap.tiers.length);

                // Add tier connector line if not first tier
                if (index > 0) {
                    const connector = document.createElement('div');
                    connector.className = 'tier-connector';
                    connector.innerHTML = `
                        <div style="display: flex; justify-content: center; margin: 0; padding: 0;">
                            <div style="width: 6px; height: 40px; background: linear-gradient(to bottom, #3b82f6, #10b981); border-radius: 3px;"></div>
                        </div>
                    `;
                    tiersContainer.appendChild(connector);
                }

                await this.renderTier(tier, index, tiersContainer);
            }

            // Final status update
            this.updateStatus(statusDiv, 'Service map complete! Displaying connections...', 'green', serviceMap.tiers.length, serviceMap.tiers.length);
            await sleep(2000);
        }

        // Display connections summary
        if (serviceMap.connections && serviceMap.connections.length > 0) {
            const connectionsDiv = this.createConnectionsDiv(serviceMap);
            mapWrapper.appendChild(connectionsDiv);

            // Trigger animation
            setTimeout(() => {
                connectionsDiv.style.opacity = '1';
                connectionsDiv.style.transform = 'translateY(0)';
                safeFeatherReplace();
            }, 100);
        }

        // Remove status indicator with fade out
        setTimeout(() => {
            statusDiv.style.transition = 'all 0.5s ease-in-out';
            statusDiv.style.opacity = '0';
            statusDiv.style.transform = 'translateY(-10px)';
            setTimeout(() => statusDiv.remove(), 500);
        }, 500);

        // Show the log viewer button if logs are available
        const btnShowLog = document.getElementById('btn-show-service-log') as HTMLElement;
        if (btnShowLog) {
            if (serviceMap.discovery_logs && serviceMap.discovery_logs.length > 0) {
                console.log('Showing log button - logs available');
                btnShowLog.style.display = 'inline-flex';
            } else {
                console.log('Not showing log button - no logs available');
            }
        }

        safeFeatherReplace();
    }

    /**
     * Create header element
     */
    private createHeader(serviceMap: ServiceMap): HTMLElement {
        const headerDiv = document.createElement('div');
        headerDiv.className = 'bg-blue-50 p-4 rounded-lg mb-4 inline-block';
        headerDiv.innerHTML = `
            <h4 class="text-lg font-semibold mb-2">${serviceMap.service_name || 'Unknown Service'}</h4>
            <div class="flex flex-wrap gap-4 text-sm">
                <div>
                    <span class="text-gray-600">Total Devices:</span>
                    <span class="font-semibold ml-2">${serviceMap.total_devices || 0}</span>
                </div>
                <div>
                    <span class="text-gray-600">Tiers:</span>
                    <span class="font-semibold ml-2">${serviceMap.tiers?.length || 0}</span>
                </div>
                <div>
                    <span class="text-gray-600">Connections:</span>
                    <span class="font-semibold ml-2">${serviceMap.connections?.length || 0}</span>
                </div>
                <div>
                    <span class="text-gray-600">Discovered:</span>
                    <span class="font-semibold ml-2">${new Date(Number(serviceMap.discovered_at) / 1_000_000).toLocaleString()}</span>
                </div>
            </div>
        `;
        return headerDiv;
    }

    /**
     * Create status indicator element
     */
    private createStatusIndicator(): HTMLElement {
        const statusDiv = document.createElement('div');
        statusDiv.id = 'service-map-status';
        statusDiv.className = 'bg-white border-2 border-blue-200 rounded-lg p-3 mb-4 text-center inline-block';
        statusDiv.innerHTML = `
            <div class="flex items-center justify-center gap-3">
                <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span class="text-gray-700 font-medium">Initializing service map...</span>
            </div>
        `;
        return statusDiv;
    }

    /**
     * Update status message
     */
    private updateStatus(statusDiv: HTMLElement, message: string, color: 'blue' | 'green' | 'purple', currentTier: number, totalTiers: number): void {
        const colors: Record<string, ColorScheme> = {
            blue: { bg: 'border-blue-200', spinner: 'border-blue-600', text: 'text-blue-700' },
            green: { bg: 'border-green-200', spinner: 'border-green-600', text: 'text-green-700' },
            purple: { bg: 'border-purple-200', spinner: 'border-purple-600', text: 'text-purple-700' }
        };
        const colorScheme = colors[color] || colors.blue;

        statusDiv.className = `bg-white border-2 ${colorScheme.bg} rounded-lg p-4 mb-4 text-center`;
        statusDiv.innerHTML = `
            <div class="flex items-center justify-center gap-3">
                <div class="animate-spin rounded-full h-5 w-5 border-b-2 ${colorScheme.spinner}"></div>
                <span class="${colorScheme.text} font-medium">${message}</span>
            </div>
            <div class="mt-2 text-sm text-gray-600">
                Tier ${currentTier} of ${totalTiers}
            </div>
        `;
    }

    /**
     * Render a single tier with animation
     */
    private async renderTier(tier: ServiceMapTier, index: number, container: HTMLElement): Promise<void> {
        const tierColors: TierColors[] = [
            { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700' },
            { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700' },
            { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700' },
            { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700' }
        ];
        const colors = tierColors[index % tierColors.length];

        const tierDiv = document.createElement('div');
        tierDiv.className = `border-2 ${colors.border} ${colors.bg} rounded-lg p-4 mb-2 inline-block`;
        tierDiv.style.opacity = '0';
        tierDiv.style.transform = 'translateY(30px)';
        tierDiv.style.transition = 'all 0.6s ease-in-out';

        // Determine grid columns
        const deviceCount = tier.devices?.length || 0;
        const gridCols = deviceCount === 1 ? 'grid-cols-1' : deviceCount === 2 ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

        let tierHtml = `
            <h5 class="font-semibold text-lg mb-3 ${colors.text} text-center">
                Tier ${tier.tier_level}: ${tier.tier_name} (${deviceCount} device${deviceCount !== 1 ? 's' : ''})
            </h5>
            <div class="grid ${gridCols} gap-3">
        `;

        if (tier.devices && tier.devices.length > 0) {
            tier.devices.forEach(device => {
                tierHtml += `
                    <div class="bg-white p-3 rounded border shadow-sm min-w-[200px]">
                        <div class="font-semibold text-blue-600 text-sm">${device.device_id || 'Unknown'}</div>
                        <div class="text-xs text-gray-600 mt-1 space-y-0.5">
                            <div><strong>IP:</strong> ${device.ip_address || 'N/A'}</div>
                            <div><strong>Host:</strong> ${device.hostname || 'N/A'}</div>
                            <div><strong>Role:</strong> ${device.role || 'Unknown'}</div>
                            ${device.config_files && device.config_files.length > 0 ? `
                                <div class="mt-1.5 pt-1.5 border-t border-gray-200">
                                    <strong>Configs (${device.config_files.length}):</strong>
                                    <ul class="text-xs mt-0.5 space-y-0.5 text-gray-500">
                                        ${device.config_files.slice(0, 2).map(f => `<li class="truncate" title="${f}">${f.split('/').pop()}</li>`).join('')}
                                        ${device.config_files.length > 2 ? `<li class="text-gray-400">+${device.config_files.length - 2} more</li>` : ''}
                                    </ul>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                `;
            });
        }

        tierHtml += `</div>`;
        tierDiv.innerHTML = tierHtml;

        container.appendChild(tierDiv);

        // Trigger animation
        await sleep(100);
        tierDiv.style.opacity = '1';
        tierDiv.style.transform = 'translateY(0)';

        return new Promise(resolve => {
            setTimeout(() => resolve(), 600);
        });
    }

    /**
     * Create connections div
     */
    private createConnectionsDiv(serviceMap: ServiceMap): HTMLElement {
        const connectionsDiv = document.createElement('div');
        connectionsDiv.className = 'border rounded-lg p-3 bg-gray-50 mt-6 inline-block max-w-2xl';
        connectionsDiv.style.opacity = '0';
        connectionsDiv.style.transform = 'translateY(20px)';
        connectionsDiv.style.transition = 'all 0.5s ease-in-out';

        let connectionsHtml = `
            <h5 class="font-semibold text-base mb-2 flex items-center gap-2 justify-center">
                <i data-feather="arrow-right"></i>
                Connections (${serviceMap.connections!.length})
            </h5>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        `;

        serviceMap.connections!.forEach(conn => {
            connectionsHtml += `
                <div class="flex items-center gap-2 text-xs p-2 bg-white rounded border">
                    <span class="font-mono text-blue-600 text-xs">${conn.from_device}</span>
                    <i data-feather="arrow-right" class="w-3 h-3 flex-shrink-0"></i>
                    <span class="font-mono text-green-600 text-xs">${conn.to_device}</span>
                    <span class="text-gray-500 text-xs whitespace-nowrap">(${conn.connection_type}${conn.port !== null ? `:${conn.port}` : ''})</span>
                </div>
            `;
        });

        connectionsHtml += `</div>`;
        connectionsDiv.innerHTML = connectionsHtml;

        return connectionsDiv;
    }

    /**
     * Show Service Model Log in modal
     */
    showLog(): void {
        console.log('showServiceLog() called');
        console.log('currentServiceMap:', this.currentServiceMap);

        if (!this.currentServiceMap || !this.currentServiceMap.discovery_logs) {
            console.error('No service map or logs available');
            alert('No service model logs available. Please generate a service map first.');
            return;
        }

        const modal = document.getElementById('service-log-modal') as HTMLElement;
        const logContent = document.getElementById('service-log-content') as HTMLElement;

        if (!modal || !logContent) {
            console.error('Modal elements not found');
            return;
        }

        // Clear previous content
        logContent.classList.remove('hidden');
        logContent.innerHTML = '';

        // Populate with logs
        const logs = this.currentServiceMap.discovery_logs;
        console.log('Number of log lines:', logs.length);

        if (logs.length === 0) {
            logContent.innerHTML = '<div class="text-gray-500 italic">No logs available</div>';
        } else {
            logs.forEach((logLine) => {
                const logDiv = document.createElement('div');
                logDiv.className = 'text-gray-800 leading-relaxed';
                logDiv.textContent = logLine;
                logContent.appendChild(logDiv);
            });
        }

        // Show modal
        modal.style.display = 'flex';
        safeFeatherReplace();
    }

    /**
     * Close Service Model Log modal
     */
    closeLog(): void {
        const modal = document.getElementById('service-log-modal') as HTMLElement;
        if (modal) {
            modal.style.display = 'none';
        }
    }

    /**
     * Copy log to clipboard
     */
    async copyLogToClipboard(): Promise<void> {
        if (!this.currentServiceMap || !this.currentServiceMap.discovery_logs) {
            alert('No logs to copy');
            return;
        }

        const logs = this.currentServiceMap.discovery_logs;
        const logText = logs.join('\n');

        try {
            await navigator.clipboard.writeText(logText);

            // Show success feedback
            const btnCopy = document.getElementById('btn-copy-log') as HTMLButtonElement;
            if (btnCopy) {
                const originalText = btnCopy.innerHTML;
                btnCopy.innerHTML = '<i data-feather="check"></i> Copied!';
                btnCopy.classList.add('btn-success');
                safeFeatherReplace();

                setTimeout(() => {
                    btnCopy.innerHTML = originalText;
                    btnCopy.classList.remove('btn-success');
                    safeFeatherReplace();
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to copy logs:', error);
            alert('Failed to copy logs to clipboard');
        }
    }

    /**
     * Get current service map
     */
    getCurrentServiceMap(): ServiceMap | null {
        return this.currentServiceMap;
    }

    /**
     * Set current service map (for external access)
     */
    setCurrentServiceMap(serviceMap: ServiceMap): void {
        this.currentServiceMap = serviceMap;
    }
}
